import {Input, Field, InputGroup} from '@chakra-ui/react'
import {NumberInputRootProps} from '@chakra-ui/react'
import {useEffect, useState} from 'react'
import type {ChangeEvent} from 'react'
import {ErrorMessage} from './error-message.js'
import {FieldRequiredIndicator} from './field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {LuClock} from 'react-icons/lu'
import {FieldRoot} from './field-root.js'

interface FieldProps extends NumberInputRootProps {
  label: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  helpText?: string
}
const isValidHour = (hour: number) => Number.isInteger(hour) && hour >= 0 && hour < 24
const isValidMinutes = (minutes: number) =>
  (Number.isInteger(minutes) && minutes >= 0 && minutes < 60) || Number.isNaN(minutes)
const isValid = (val: string) => {
  const regexp = /^\d{0,2}?:?\d{0,2}$/

  const [hoursStr, minutesStr] = val.split(':')

  if (!regexp.test(val)) {
    return false
  }

  const hours = Number(hoursStr)
  const minutes = Number(minutesStr)

  if (!isValidHour(hours) || !isValidMinutes(minutes)) {
    return false
  }

  if (minutes < 10 && Number(minutesStr[0]) > 5) {
    return false
  }

  const valArr = val.indexOf(':') !== -1 ? val.split(':') : [val]

  // check mm and HH
  if (valArr[0] && valArr[0].length && (Number.parseInt(valArr[0], 10) < 0 || Number.parseInt(valArr[0], 10) > 23)) {
    return false
  }

  if (valArr[1] && valArr[1].length && (Number.parseInt(valArr[1], 10) < 0 || Number.parseInt(valArr[1], 10) > 59)) {
    return false
  }

  return true
}

export function HourInput({label, required, helpText, disabled, placeholder, ...props}: FieldProps) {
  const field = useFieldContext<string>()
  const [lastVal, setLastVal] = useState('')

  // Legacy/backend data can arrive as "HH:MM:SS" (or otherwise outside the "HH:MM" shape this
  // input edits). Since the input is controlled by field.state.value and every edit is validated
  // against that exact shape, a value that never matches it makes the field permanently
  // unresponsive to typing. Normalize it once so editing works again.
  useEffect(() => {
    const value = field.state.value
    if (!value || /^\d{2}:\d{2}$/.test(value)) return
    const match = value.match(/^(\d{2}):(\d{2})/)
    if (match) field.handleChange(`${match[1]}:${match[2]}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.state.value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value ?? ''
    if (val === field.state.value) {
      return
    }
    if (isValid(val)) {
      if (val.length === 2 && lastVal.length !== 3 && val.indexOf(':') === -1) {
        val = val + ':'
      }

      if (val.length === 2 && lastVal.length === 3) {
        val = val.slice(0, 1)
      }

      if (val.length > 5) {
        return false
      }

      setLastVal(val)

      field.handleChange(val)

      if (val.length === 5) {
        field.handleChange(val)
      }
    }
  }

  return (
    <FieldRoot required={required} disabled={disabled}>
      <Field.Label>
        {label}
        {required && <FieldRequiredIndicator />}
      </Field.Label>
      <InputGroup startElement={<LuClock />}>
        <Input
          name={field.name}
          backgroundColor="bg"
          value={field.state.value}
          onChange={handleChange}
          placeholder={placeholder ?? 'HH:mm'}
          inputMode="numeric"
          maxLength={5}
          onBlur={() => field.handleBlur()}
          {...props}
        />
      </InputGroup>
      {helpText && <Field.HelperText>{helpText}</Field.HelperText>}
      <ErrorMessage />
    </FieldRoot>
  )
}
